import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CheckCircleIcon,
  PencilIcon,
  PlayIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { HsePageChrome, useHseBusyAction } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { errorStateVariantFromError } from "../../../../components/utils/errorStateUtils";
import {
  completeHseTiltak,
  exportHseTiltakPdf,
  fetchHseTiltakItem,
  startHseTiltak,
} from "../../../../api/hse/tiltak";
import {
  isTiltakOverdue,
  tiltakFromRecord,
} from "../../../../components/dashboard/hse/hseTiltakUtils";

export const HseKsTiltakView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorVariant, setErrorVariant] = useState("load_error");
  const [item, setItem] = useState(null);
  const { run, busyProps } = useHseBusyAction();
  const load = async () => {
    try {
      setLoading(true);
      setError(false);
      const record = await fetchHseTiltakItem(id);
      setItem(tiltakFromRecord(record));
    } catch (err) {
      console.error(err);
      setError(true);
      setErrorVariant(errorStateVariantFromError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleStart = () =>
    run("start", async () => {
      try {
        await startHseTiltak(id);
        toast.success(t("hse.tiltak.started"));
        load();
      } catch (err) {
        toast.error(err.message || t("hse.tiltak.save_failed"));
      }
    });

  const handleComplete = () =>
    run("complete", async () => {
      try {
        await completeHseTiltak(id);
        toast.success(t("hse.tiltak.completed"));
        load();
      } catch (err) {
        toast.error(err.message || t("hse.tiltak.save_failed"));
      }
    });

  const handlePrint = () =>
    run("pdf", async () => {
      try {
        await exportHseTiltakPdf(id, `hse-tiltak-${id}.pdf`);
      } catch (err) {
        toast.error(err.message || t("hse.tiltak.pdf_failed"));
      }
    });

  const overdue = isTiltakOverdue(item);

  return (
    <HsePageChrome
      title={item?.title || t("hse.tiltak.title")}
      subtitle={t("hse.tiltak.view_subtitle")}
      backTo="/dashboard/hse-ks/tiltak"
      mainButton={{
        label: t("hse.tiltak.edit"),
        action: () => navigate(`/dashboard/hse-ks/tiltak/${id}/edit`),
        icon: PencilIcon,
        hidden: loading || error,
      }}
      actions={[
        {
          label: t("hse.tiltak.print_action"),
          action: handlePrint,
          icon: PrinterIcon,
          hidden: loading || error || !item,
          menu: false,
          ...busyProps("pdf"),
        },
        {
          label: t("hse.tiltak.start"),
          action: handleStart,
          icon: PlayIcon,
          hidden: loading || !item || !["planned", "on_hold"].includes(item.status),
          menu: false,
          ...busyProps("start"),
        },
        {
          label: t("hse.tiltak.complete"),
          action: handleComplete,
          icon: CheckCircleIcon,
          hidden: loading || !item || !["started", "in_progress"].includes(item.status),
          menu: false,
          ...busyProps("complete"),
        },
      ]}
    >
      <div className="mx-auto mt-8 max-w-3xl px-4 sm:px-6 lg:px-8">
        {loading ? (
          <ResourceLoading />
        ) : error || !item ? (
          <ResourceError
            variant={!item && !error ? "not_found" : errorVariant}
            errors={errorVariant === "load_error" && error ? [t("hse.tiltak.load_failed")] : undefined}
            onRetry={error ? load : undefined}
            backTo="/dashboard/hse-ks/tiltak"
          />
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("hse.tiltak.field.action_type")}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {t(`hse.tiltak.type.${item.action_type || "generic"}`)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("hse.tiltak.col_status")}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {overdue
                      ? t("hse.tiltak.status_overdue")
                      : t(`hse.tiltak.status.${item.status || "planned"}`)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("hse.tiltak.field.assignee")}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {item.responsible_user?.displayTitle ||
                      item.responsible_user?.name ||
                      item.assignee?.name ||
                      "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("hse.tiltak.field.deadline")}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {item.deadline ? new Date(item.deadline).toLocaleDateString() : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("hse.tiltak.field.priority")}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {t(`hse.tiltak.priority.${item.priority || "medium"}`)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("hse.tiltak.field.category")}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {t(`hse.tiltak.category.${item.category || "other"}`)}
                  </dd>
                </div>
              </dl>

              {item.description ? (
                <div className="mt-6">
                  <h2 className="text-sm font-semibold text-slate-900">
                    {t("hse.tiltak.field.description")}
                  </h2>
                  <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
                    {item.description}
                  </p>
                </div>
              ) : null}

              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                {item.hse_event_id ? (
                  <Link
                    to={`/dashboard/hse-ks/events/${item.hse_event_id}`}
                    className="hse-no-print text-[#1463C7] hover:underline"
                  >
                    {t("hse.tiltak.linked_event")}
                  </Link>
                ) : null}
                {item.hse_document_id ? (
                  <Link
                    to={`/dashboard/hse-ks/documents/${item.hse_document_id}`}
                    className="hse-no-print text-[#1463C7] hover:underline"
                  >
                    {t("hse.tiltak.linked_document")}
                  </Link>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </HsePageChrome>
  );
};
